import * as React from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Star } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useSaveDebrief, type DebriefUpdate, type EventResponse } from '@/hooks/useEvents'
import { fmtDate } from '@/lib/dates'

// Post-Event Debrief (spec §Post-Event Debrief) — structured form Gina fills
// within a day or two after each event. All fields optional; numeric fields
// held as strings and converted in onSubmit (same convention as EventFormModal).

const WEATHER_OPTIONS = ['Sunny', 'Partly Cloudy', 'Overcast', 'Rain', 'Cold', 'Extreme Heat']

const optionalNum = (min: number, label: string) =>
  z.string().refine(
    (v) => v.trim() === '' || (Number.isFinite(Number(v)) && Number(v) >= min),
    { message: `${label} must be ≥ ${min}` }
  )

const debriefSchema = z.object({
  rating: z.string(), // '' or '1'..'5'
  weather: z.array(z.string()),
  attendance: optionalNum(0, 'Attendance'),
  competition: z.string(),
  worked: z.string(),
  didnt_work: z.string(),
  attend_again: z.enum(['', 'yes', 'no', 'maybe']),
})

type DebriefValues = z.infer<typeof debriefSchema>

const EMPTY: DebriefValues = {
  rating: '',
  weather: [],
  attendance: '',
  competition: '',
  worked: '',
  didnt_work: '',
  attend_again: '',
}

const textareaCls =
  'min-h-[56px] w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 py-1.5 text-sm ' +
  'placeholder:text-[var(--mute)] focus-visible:outline-none focus:border-[var(--brand)] disabled:opacity-50'

export function DebriefModal({
  open,
  onOpenChange,
  event,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  event: EventResponse | null
}) {
  const saveDebrief = useSaveDebrief()
  const busy = saveDebrief.isPending

  const form = useForm<DebriefValues>({
    resolver: zodResolver(debriefSchema),
    defaultValues: EMPTY,
  })

  React.useEffect(() => {
    if (open) {
      form.reset(EMPTY)
      saveDebrief.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, event])

  async function onSubmit(values: DebriefValues) {
    if (!event) return
    const body: DebriefUpdate = {}
    if (values.rating) body.rating = Number(values.rating)
    if (values.weather.length > 0) body.weather = values.weather
    if (values.attendance.trim() !== '') body.attendance = Math.trunc(Number(values.attendance))
    if (values.competition.trim() !== '') body.competition = values.competition.trim()
    if (values.worked.trim() !== '') body.worked = values.worked.trim()
    if (values.didnt_work.trim() !== '') body.didnt_work = values.didnt_work.trim()
    if (values.attend_again !== '') body.attend_again = values.attend_again
    try {
      await saveDebrief.mutateAsync({ eventId: event.id, body })
      toast.success('Debrief saved')
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save debrief')
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="brand-theme border-[var(--line-warm)] bg-[var(--surface-card)] sm:max-w-[520px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Post-event debrief</DialogTitle>
          <DialogDescription>
            {event ? `${event.location_name ?? ''} — ${fmtDate(event.event_date)}. ` : ''}
            Quick structured notes while the event is fresh. Every field is optional.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-3">
              <FormField
                control={form.control}
                name="rating"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Overall rating</FormLabel>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <button
                          key={n}
                          type="button"
                          disabled={busy}
                          onClick={() => field.onChange(field.value === String(n) ? '' : String(n))}
                          aria-label={`${n} star${n > 1 ? 's' : ''}`}
                          className="p-0.5"
                        >
                          <Star
                            className={`h-5 w-5 ${
                              field.value !== '' && Number(field.value) >= n
                                ? 'fill-[var(--brand)] text-[var(--brand)]'
                                : 'text-[var(--mute)]'
                            }`}
                          />
                        </button>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="attend_again"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Would attend again?</FormLabel>
                    <FormControl>
                      <select
                        {...field}
                        disabled={busy}
                        className="h-9 w-full border border-[var(--line-input)] bg-[var(--surface-card)] px-2 text-sm focus:outline-none focus:border-[var(--brand)]"
                      >
                        <option value="">— undecided —</option>
                        <option value="yes">Yes</option>
                        <option value="no">No</option>
                        <option value="maybe">Maybe</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="weather"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Weather</FormLabel>
                  <div className="grid grid-cols-3 gap-1">
                    {WEATHER_OPTIONS.map((w) => (
                      <label key={w} className="inline-flex items-center gap-1.5 text-xs">
                        <input
                          type="checkbox"
                          disabled={busy}
                          checked={field.value.includes(w)}
                          onChange={(e) =>
                            field.onChange(
                              e.target.checked
                                ? [...field.value, w]
                                : field.value.filter((x: string) => x !== w)
                            )
                          }
                        />
                        {w}
                      </label>
                    ))}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="attendance"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Estimated attendance{' '}
                    <span className="font-normal text-[var(--mute)]">(horses/riders)</span>
                  </FormLabel>
                  <FormControl>
                    <Input type="number" min={0} {...field} disabled={busy} className="w-32" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="competition"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendor competition</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      disabled={busy}
                      placeholder="Which other vendors were present? Same brands?"
                      className={textareaCls}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="worked"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What worked</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      disabled={busy}
                      placeholder="Products, displays, pricing that felt right"
                      className={textareaCls}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="didnt_work"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>What didn&apos;t work</FormLabel>
                  <FormControl>
                    <textarea
                      {...field}
                      disabled={busy}
                      placeholder="Products that didn't move, booth issues…"
                      className={textareaCls}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={busy}
              >
                Cancel
              </Button>
              <Button type="submit" size="sm" disabled={busy} data-testid="debrief-submit">
                {busy ? 'Saving…' : 'Save debrief'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
